import React, { useState } from 'react';

import Button from '../../../components/ui/Button';
import type { APIEndpoint, CodeExample } from '../types';

const APIDocumentationSection: React.FC = () => {
  const [selectedEndpoint, setSelectedEndpoint] = useState<string>('analytics');

  const apiEndpoints: APIEndpoint[] = [
    {
      id: 'analytics',
      method: 'POST',
      endpoint: '/api/v1/analytics/process',
      description: 'Process video stream and return analytics data',
      parameters: [
        { name: 'stream_url', type: 'string', required: true, description: 'Video stream URL or file path' },
        { name: 'analytics_type', type: 'string[]', required: true, description: 'Types of analytics to perform' },
        { name: 'confidence_threshold', type: 'number', required: false, description: 'Minimum confidence score (0-1)' }
      ],
      response: '{\n  "status": "success",\n  "data": {\n    "objects": [...],\n    "analytics": {...}\n  }\n}'
    },
    {
      id: 'detection',
      method: 'POST',
      endpoint: '/api/v1/detection/objects',
      description: 'Detect objects in a single frame or image',
      parameters: [
        { name: 'image', type: 'file', required: true, description: 'Image file or base64 encoded string' },
        { name: 'classes', type: 'string[]', required: false, description: 'Specific object classes to detect' },
        { name: 'return_crops', type: 'boolean', required: false, description: 'Return cropped images of detected objects' }
      ],
      response: '{\n  "detections": [\n    {\n      "class": "person",\n      "confidence": 0.95,\n      "bbox": [x, y, w, h]\n    }\n  ]\n}'
    },
    {
      id: 'tracking',
      method: 'GET',
      endpoint: '/api/v1/tracking/status',
      description: 'Get real-time tracking status for active streams',
      parameters: [
        { name: 'stream_id', type: 'string', required: true, description: 'Unique stream identifier' },
        { name: 'include_history', type: 'boolean', required: false, description: 'Include tracking history' }
      ],
      response: '{\n  "stream_id": "abc123",\n  "active_tracks": 15,\n  "tracks": [...]\n}'
    }
  ];

  const codeExamples: CodeExample[] = [
    {
      id: 'python',
      title: 'Python SDK',
      language: 'python',
      code: `import visionanalytics

# Initialize client
client = visionanalytics.Client(api_key="your_api_key")

# Process video stream
result = client.analytics.process(
    stream_url="rtsp://camera.example.com/stream",
    analytics_type=["object_detection", "crowd_counting"],
    confidence_threshold=0.85
)

print(result.data)`,
      description: 'Python SDK for easy integration with Camlytix AI'
    },
    {
      id: 'javascript',
      title: 'JavaScript SDK',
      language: 'javascript',
      code: `import VisionAnalytics from '@visionanalytics/sdk';

// Initialize client
const client = new VisionAnalytics({
  apiKey: 'your_api_key'
});

// Process video stream
const result = await client.analytics.process({
  streamUrl: 'rtsp://camera.example.com/stream',
  analyticsType: ['object_detection', 'crowd_counting'],
  confidenceThreshold: 0.85
});

console.log(result.data);`,
      description: 'JavaScript/TypeScript SDK for web and Node.js applications'
    },
    {
      id: 'curl',
      title: 'cURL',
      language: 'bash',
      code: `curl -X POST https://api.visionanalytics.com/v1/analytics/process \\
  -H "Authorization: Bearer your_api_key" \ -H"Content-Type: application/json" \\
  -d '{
    "stream_url": "rtsp://camera.example.com/stream",
    "analytics_type": ["object_detection", "crowd_counting"],
    "confidence_threshold": 0.85
  }'`,
      description: 'Direct API access using cURL for testing and integration'
    }
  ];

  return (
    <section className="py-20 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            API Documentation
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            RESTful APIs with comprehensive documentation and SDKs for seamless integration
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 mb-12">
          <div>
            <h3 className="text-xl font-bold text-foreground mb-4">API Endpoints</h3>
            <div className="space-y-3">
              {apiEndpoints.map((endpoint) => (
                <button
                  key={endpoint.id}
                  onClick={() => setSelectedEndpoint(endpoint.id)}
                  className={`w-full text-left p-4 rounded-lg transition-all duration-300 ${
                    selectedEndpoint === endpoint.id
                      ? 'bg-primary text-primary-foreground shadow-brand'
                      : 'bg-card hover:bg-muted text-foreground border border-border'
                  }`}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <span className={`px-2 py-1 text-xs font-bold rounded ${
                      endpoint.method === 'GET' ?'bg-success/20 text-success' :'bg-warning/20 text-warning'
                    }`}>
                      {endpoint.method}
                    </span>
                    <code className={`text-sm font-mono ${
                      selectedEndpoint === endpoint.id ? 'text-primary-foreground' : 'text-foreground'
                    }`}>
                      {endpoint.endpoint}
                    </code>
                  </div>
                  <p className={`text-sm ${
                    selectedEndpoint === endpoint.id ? 'text-primary-foreground/80' : 'text-text-secondary'
                  }`}>
                    {endpoint.description}
                  </p>
                </button>
              ))}
            </div>
          </div>

          <div>
            {apiEndpoints.map((endpoint) => (
              <div
                key={endpoint.id}
                className={`${
                  selectedEndpoint === endpoint.id ? 'block' : 'hidden'
                } bg-card rounded-lg p-6 shadow-brand border border-border`}
              >
                <h4 className="text-lg font-bold text-foreground mb-4">Parameters</h4>
                <div className="space-y-3 mb-6">
                  {endpoint.parameters.map((param, index) => (
                    <div key={index} className="p-3 bg-background rounded-lg border border-border">
                      <div className="flex items-center gap-2 mb-1">
                        <code className="text-sm font-mono text-primary">{param.name}</code>
                        <span className="text-xs text-text-secondary">{param.type}</span>
                        {param.required && (
                          <span className="text-xs px-2 py-0.5 bg-error/20 text-error rounded">Required</span>
                        )}
                      </div>
                      <p className="text-sm text-text-secondary">{param.description}</p>
                    </div>
                  ))}
                </div>

                <h4 className="text-lg font-bold text-foreground mb-4">Response</h4>
                <div className="bg-authority-charcoal rounded-lg p-4 overflow-x-auto">
                  <pre className="text-sm text-primary-foreground font-mono">
                    <code>{endpoint.response}</code>
                  </pre>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-card rounded-lg p-8 shadow-brand border border-border">
          <h3 className="text-2xl font-bold text-foreground mb-6">Code Examples</h3>
          <div className="grid md:grid-cols-3 gap-4 mb-6">
            {codeExamples.map((example) => (
              <div key={example.id} className="space-y-2">
                <h4 className="font-semibold text-foreground">{example.title}</h4>
                <div className="bg-authority-charcoal rounded-lg p-4 overflow-x-auto">
                  <pre className="text-xs text-primary-foreground font-mono whitespace-pre-wrap">
                    <code>{example.code}</code>
                  </pre>
                </div>
                <p className="text-sm text-text-secondary">{example.description}</p>
              </div>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center pt-6 border-t border-border">
            <Button variant="default" iconName="Book" iconPosition="left">
              Full API Documentation
            </Button>
            <Button variant="outline" iconName="Download" iconPosition="left">
              Download SDK
            </Button>
            <Button variant="outline" iconName="Code" iconPosition="left">
              View on GitHub
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default APIDocumentationSection;